/**
 * Point d'entrée unique pour le retour de l'app au premier plan.
 *
 * Sur device, un resume déclenchait trois handlers qui se recouvraient
 * (focus/visibilitychange web dans main.ts + appStateChange Capacitor dans
 * App.vue) : jusqu'à 3 loadHouseholdChildren et un burst de refreshes lancés
 * en concurrence avec la première interaction de l'utilisateur, sur une
 * WKWebView qui vient de rétablir ses sockets.
 */
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/lib/supabase';
import { eventBus } from '@/utils/eventBus';
import { checkPremiumExpiration } from '@/services/premium.service';
import { feedbackService } from '@/services/feedback.service';
import { useAuthStore } from '@/stores/auth';
import { useAppContextStore } from '@/stores/appContext';
import { useChildAuthStore } from '@/stores/childAuth';

// Fenêtre pendant laquelle les notifications de resume (focus web et
// appStateChange natif arrivent tous les deux sur device) comptent comme un
// seul événement.
const RESUME_DEDUPE_MS = 3000;
// Laisse la première interaction (tap d'onglet, animation de navigation)
// passer avant le burst de refreshes silencieux.
const REFRESH_DELAY_MS = 400;

let lastHandledAt = 0;

export function notifyAppPaused(): void {
    // Le timer d'auto-refresh du token est gelé quand la WKWebView est
    // suspendue ; on l'arrête/relance explicitement comme recommandé par
    // Supabase pour Capacitor.
    if (Capacitor.isNativePlatform()) {
        void supabase.auth.stopAutoRefresh();
    }
}

export function notifyAppResumed(): void {
    // Refresh proactif du token — sinon il part lazy sur la première requête
    // et s'ajoute à la latence du premier tap.
    if (Capacitor.isNativePlatform()) {
        void supabase.auth.startAutoRefresh();
    }

    const now = Date.now();
    if (now - lastHandledAt < RESUME_DEDUPE_MS) return;
    lastHandledAt = now;

    setTimeout(() => { void runResumeRefresh(); }, REFRESH_DELAY_MS);
}

async function runResumeRefresh(): Promise<void> {
    try {
        const authStore = useAuthStore();
        if (!authStore.user) return;

        // Téléphone de l'enfant retiré par le parent : retour à l'accueil.
        const { releaseIfPairedDeviceRevoked } = await import('@/services/paired-device-guard');
        if (await releaseIfPairedDeviceRevoked()) return;

        // Les comptes enfants n'ont pas accès aux RPC household_members :
        // on émet seulement app-resumed, les pages enfant se rafraîchissent.
        if (useChildAuthStore().isAuthenticated) return;

        // Retour au premier plan : alimente la détection « retour après
        // plusieurs jours d'absence » de la demande d'avis native (TW #17712331).
        feedbackService.recordAppActive();

        const appContextStore = useAppContextStore();
        const h = appContextStore.currentHousehold;
        if (h) {
            try {
                await checkPremiumExpiration(h);
            } catch (error) {
                console.error('[AppResume] Premium expiration check failed:', error);
            }
            // Doublon potentiel avec recoverContextOnFocus : dédupliqué par le
            // cache de children.service.
            await appContextStore.loadHouseholdChildren(h.id);
        }

        await appContextStore.recoverContextOnFocus();
        await appContextStore.refreshCurrentPageData();
    } catch (error) {
        console.error('[AppResume] Error refreshing after resume:', error);
    } finally {
        // Les pages montées écoutent app-resumed et font leur refresh silencieux.
        eventBus.emit('app:app-resumed', undefined);
    }
}
